/**
 * OnboardingConfirmPage
 * 
 * Step 2 (final) of onboarding wizard - confirms user choice and writes to Firestore.
 * 
 * Flow:
 * 1. Reads sessionStorage data from OnboardingBasicPage
 * 2. Displays confirmation summary (state selection)
 * 3. On confirm, writes to /users/{uid} document:
 *    - onboarding.basicInfoCompleted = true
 *    - onboarding.completedAt = Timestamp.now()
 *    - profile.state (selected state)
 * 4. Redirects to dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { CheckCircle, MapPin, AlertCircle } from 'lucide-react';
import { AuthWizardLayout } from '@/layout/AuthWizardLayout';
import { useAuthUser } from '@/hooks/useAuthUser';
import { db } from '@/firebase';

interface OnboardingData {
  state: string | null;
}

export function OnboardingConfirmPage() {
  const navigate = useNavigate();
  const { firebaseUser, userDoc } = useAuthUser();

  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load onboarding data from sessionStorage
  useEffect(() => {
    const storedData = sessionStorage.getItem('onboarding_data');
    if (!storedData) {
      // No data found - redirect back to basic page
      navigate('/onboarding/basic');
      return;
    }

    try {
      const data = JSON.parse(storedData) as OnboardingData;
      setOnboardingData(data);
    } catch (err) {
      console.error('[OnboardingConfirm] Failed to parse stored data:', err);
      navigate('/onboarding/basic');
    }
  }, [navigate]);

  const handleConfirm = async () => {
    if (!firebaseUser || !userDoc || !onboardingData) {
      setError('Authentication error. Please refresh and try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const now = Timestamp.now();

      const updateData: Record<string, unknown> = {
        'onboarding.basicInfoCompleted': true,
        'onboarding.completedAt': now,
        'onboarding.lastCompletedStep': 'confirm',
        'profile.state': onboardingData.state,
        updatedAt: now,
      };

      await updateDoc(userRef, updateData);

      // Clear sessionStorage
      sessionStorage.removeItem('onboarding_data');

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('[OnboardingConfirm] Failed to save onboarding data:', err);
      setError('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/basic');
  };

  if (!onboardingData) {
    return (
      <AuthWizardLayout title="Loading...">
        <div className="text-center text-slate-400">Loading your information...</div>
      </AuthWizardLayout>
    );
  }

  return (
    <AuthWizardLayout
      title="Confirm Your Selection"
      subtitle="Please review before continuing."
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="space-y-3">
          {/* State Summary */}
          <SummaryCard
            icon={<MapPin className="w-5 h-5" />}
            label="Case Filing State"
            value={onboardingData.state || 'Not specified'}
          />
        </div>

        {/* Info Box */}
        <div className="flex gap-3 p-4 bg-blue-900/20 border border-blue-500/50 rounded-lg">
          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-100">
            <p className="font-semibold mb-1">You're almost done!</p>
            <p>
              Click <strong>Continue</strong> to complete your setup and start using ThreadLock.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex gap-3 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-200">{error}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={loading}
            className="flex-1 py-3 px-4 bg-[#1a2332] border border-slate-600 text-slate-200 font-semibold rounded-lg hover:bg-[#1a2332]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 py-3 px-4 bg-[#C2410C] text-white font-semibold rounded-lg shadow-md hover:bg-[#A0330A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </AuthWizardLayout>
  );
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function SummaryCard({ icon, label, value }: SummaryCardProps) {
  return (
    <div className="flex items-center gap-4 p-4 bg-[#1a2332] border border-slate-600 rounded-lg">
      <div className="flex-shrink-0 text-[#C2410C]">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-white mt-1">{value}</p>
      </div>
    </div>
  );
}
